import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faTag } from '@fortawesome/free-solid-svg-icons';
import {
  equipmentCategoryService,
  type EquipmentCategory,
  type CreateEquipmentCategoryPayload,
} from '@/services/equipment-category.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

/** Exported for reuse by the inline creation modals (Phase C.3) — the intake
 *  workflow must never duplicate this form. */
export function CategoryForm({
  initial,
  onSubmit,
  onCancel,
  saving,
}: {
  initial?: Partial<CreateEquipmentCategoryPayload>;
  onSubmit: (d: CreateEquipmentCategoryPayload) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [nameErr, setNameErr] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameErr('Name is required');
      return;
    }
    setNameErr('');
    onSubmit({ name, description: description || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Name" htmlFor="ec-name" required error={nameErr}>
        <Input
          id="ec-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="HVAC, Electrical..."
          disabled={saving}
        />
      </FormField>
      <FormField label="Description" htmlFor="ec-desc">
        <Textarea
          id="ec-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={saving}
        />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function EquipmentCategoriesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EquipmentCategory | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['equipment-categories', page, debouncedSearch],
    queryFn: () =>
      equipmentCategoryService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['equipment-categories'] });

  const createMutation = useMutation({
    mutationFn: equipmentCategoryService.create,
    onSuccess: () => {
      toast.success('Category created');
      setModalOpen(false);
      invalidate();
    },
    onError: () => toast.error('Failed to create category'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<CreateEquipmentCategoryPayload>;
    }) => equipmentCategoryService.update(id, payload),
    onSuccess: () => {
      toast.success('Category updated');
      setModalOpen(false);
      setEditTarget(null);
      invalidate();
    },
    onError: () => toast.error('Failed to update category'),
  });

  const deleteMutation = useMutation({
    mutationFn: equipmentCategoryService.remove,
    onSuccess: () => {
      toast.success('Category deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete category'),
  });

  const handleDelete = async (c: EquipmentCategory) => {
    if (await confirmDelete(c.name)) deleteMutation.mutate(c.id);
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (c: EquipmentCategory) => {
    setEditTarget(c);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Equipment Categories"
        description="Organise equipment into logical categories"
        onAdd={can('category:create') ? openCreate : undefined}
        addLabel="Add Category"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search categories..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Description</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-muted-foreground">
                      {c.description ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${c.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}
                      >
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('category:update') && (
                          <button
                            onClick={() => openEdit(c)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${c.name}`}
                          >
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('category:delete') && (
                          <button
                            onClick={() => handleDelete(c)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${c.name}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faTag}
          title={search ? 'No categories match your search' : 'No categories yet'}
          description={
            search ? 'Try a different search term.' : 'Add your first equipment category.'
          }
          action={
            !search && can('category:create') ? (
              <Button onClick={openCreate}>Add Category</Button>
            ) : undefined
          }
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? 'Edit Category' : 'Add Category'}
      >
        <CategoryForm
          initial={
            editTarget
              ? { name: editTarget.name, description: editTarget.description ?? undefined }
              : undefined
          }
          onSubmit={(p) =>
            editTarget
              ? updateMutation.mutate({ id: editTarget.id, payload: p })
              : createMutation.mutate(p)
          }
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
